import type { RoomParticipant } from "@topzle/types";
import { runtimeId } from "./runtime-id";

export interface PeerMediaSignal {
  signalId?: string;
  fromId?: string;
  targetId: string;
  description?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
  preferences?: { receiveVideo: boolean };
}

export interface PeerToPeerRoomCallOptions {
  participantId: string;
  signal: (
    targetId: string,
    signal: Omit<PeerMediaSignal, "fromId" | "targetId">,
  ) => boolean | Promise<boolean>;
  onPublished: (media: {
    mediaSessionId?: string;
    audioTrackId?: string;
    videoTrackId?: string;
  }) => void;
  onRemoteStreams: (streams: Map<string, MediaStream>) => void;
  onError: (message: string) => void;
}

export interface PeerGroupMediaProfile {
  audioBitrate: number;
  captureHeight: number;
  captureWidth: number;
  maxFramerate: number;
  scaleResolutionDownBy: number;
  videoBitrate: number;
}

/** Keeps an avatar-sized P2P mesh inside a predictable per-user upload budget. */
export function getPeerGroupMediaProfile(peerCount: number): PeerGroupMediaProfile {
  if (peerCount >= 3)
    return {
      audioBitrate: 16_000,
      captureHeight: 160,
      captureWidth: 160,
      maxFramerate: 9,
      scaleResolutionDownBy: 1.5,
      videoBitrate: 64_000,
    };
  if (peerCount === 2)
    return {
      audioBitrate: 18_000,
      captureHeight: 192,
      captureWidth: 192,
      maxFramerate: 10,
      scaleResolutionDownBy: 1.25,
      videoBitrate: 84_000,
    };
  return {
    audioBitrate: 24_000,
    captureHeight: 240,
    captureWidth: 240,
    maxFramerate: 12,
    scaleResolutionDownBy: 1,
    videoBitrate: 120_000,
  };
}

interface PeerState {
  connection: RTCPeerConnection;
  stream: MediaStream;
  senders: Partial<Record<"audio" | "video", RTCRtpSender>>;
  makingOffer: boolean;
  ignoreOffer: boolean;
  settingRemoteAnswer: boolean;
  polite: boolean;
  pendingCandidates: RTCIceCandidateInit[];
  reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  remoteWantsVideo: boolean;
  localWantsRemoteVideo: boolean;
  mediaWatchTimer: ReturnType<typeof setTimeout> | undefined;
}

const DIRECT_ICE_SERVERS: RTCIceServer[] = [
  { urls: ["stun:stun.cloudflare.com:3478", "stun:stun.l.google.com:19302"] },
];

function liveTrack(track: MediaStreamTrack | undefined): MediaStreamTrack | undefined {
  return track?.readyState === "live" ? track : undefined;
}

export class PeerToPeerRoomCall {
  private readonly peers = new Map<string, PeerState>();
  private readonly participants = new Map<string, RoomParticipant>();
  private readonly mediaRecoveryAttempted = new Set<string>();
  private audioTrack: MediaStreamTrack | undefined;
  private videoTrack: MediaStreamTrack | undefined;
  private stopped = false;
  private readonly seenSignalIds = new Set<string>();

  constructor(private readonly options: PeerToPeerRoomCallOptions) {}

  async publish(audio?: MediaStreamTrack, video?: MediaStreamTrack): Promise<void> {
    this.audioTrack = liveTrack(audio);
    this.videoTrack = liveTrack(video);
    await this.applyCaptureProfile();
    this.publishLocalState();
    for (const participant of this.participants.values()) {
      if (this.shouldConnect(participant))
        await this.syncLocalTracks(this.ensurePeer(participant.id));
    }
  }

  syncParticipants(participants: RoomParticipant[]): void {
    this.participants.clear();
    for (const participant of participants) this.participants.set(participant.id, participant);
    for (const [participantId] of this.peers) {
      const participant = this.participants.get(participantId);
      if (!participant || !this.shouldConnect(participant)) {
        this.mediaRecoveryAttempted.delete(participantId);
        this.closePeer(participantId);
      }
    }
    for (const participant of participants) {
      if (!this.shouldConnect(participant)) continue;
      const peer = this.ensurePeer(participant.id);
      this.watchExpectedRemoteMedia(participant.id, peer);
      void this.syncLocalTracks(peer).catch((error) =>
        this.report(error, "The direct call could not connect"),
      );
    }
    void this.applyCaptureProfile();
  }

  restartConnections(): void {
    this.mediaRecoveryAttempted.clear();
    for (const participantId of [...this.peers.keys()]) this.closePeer(participantId, false);
    this.emitRemoteStreams();
    for (const participant of this.participants.values()) {
      if (!this.shouldConnect(participant)) continue;
      void this.syncLocalTracks(this.ensurePeer(participant.id)).catch((error) =>
        this.report(error, "The direct call could not reconnect"),
      );
    }
  }

  setMediaPreferences(input: { activeSpeakerIds?: string[]; hiddenVideoIds?: string[] }): void {
    const hidden = new Set(input.hiddenVideoIds ?? []);
    for (const [participantId, peer] of this.peers) {
      const enabled = !hidden.has(participantId);
      for (const track of peer.stream.getVideoTracks()) {
        if (track.enabled !== enabled) track.enabled = enabled;
      }
      if (peer.localWantsRemoteVideo !== enabled) {
        peer.localWantsRemoteVideo = enabled;
        this.send(participantId, { preferences: { receiveVideo: enabled } });
      }
    }
  }

  async handleSignal(signal: PeerMediaSignal): Promise<void> {
    if (this.stopped || !signal.fromId || signal.fromId === this.options.participantId) return;
    if (signal.targetId !== this.options.participantId) return;
    if (signal.signalId) {
      if (this.seenSignalIds.has(signal.signalId)) return;
      this.seenSignalIds.add(signal.signalId);
      if (this.seenSignalIds.size > 256) {
        const oldest = this.seenSignalIds.values().next().value;
        if (oldest) this.seenSignalIds.delete(oldest);
      }
    }
    const peer = this.ensurePeer(signal.fromId);
    const connection = peer.connection;
    try {
      if (signal.description) {
        const readyForOffer =
          !peer.makingOffer && (connection.signalingState === "stable" || peer.settingRemoteAnswer);
        const collision = signal.description.type === "offer" && !readyForOffer;
        peer.ignoreOffer = !peer.polite && collision;
        if (peer.ignoreOffer) return;
        peer.settingRemoteAnswer = signal.description.type === "answer";
        try {
          await connection.setRemoteDescription(signal.description);
        } finally {
          peer.settingRemoteAnswer = false;
        }
        for (const candidate of peer.pendingCandidates.splice(0))
          await connection.addIceCandidate(candidate);
        if (signal.description.type === "offer") {
          await connection.setLocalDescription();
          if (connection.localDescription)
            this.send(signal.fromId, { description: connection.localDescription.toJSON() });
        }
      } else if (signal.candidate) {
        if (!connection.remoteDescription) {
          peer.pendingCandidates.push(signal.candidate);
          return;
        }
        try {
          await connection.addIceCandidate(signal.candidate);
        } catch (error) {
          if (!peer.ignoreOffer) throw error;
        }
      } else if (signal.preferences) {
        peer.remoteWantsVideo = signal.preferences.receiveVideo;
        await this.syncLocalTracks(peer);
      }
    } catch (error) {
      this.report(error, "The direct call negotiation failed");
    }
  }

  stop(): void {
    this.stopped = true;
    this.mediaRecoveryAttempted.clear();
    for (const participantId of [...this.peers.keys()]) this.closePeer(participantId);
    this.options.onRemoteStreams(new Map());
    this.options.onPublished({});
  }

  private shouldConnect(participant: RoomParticipant): boolean {
    if (
      participant.id === this.options.participantId ||
      participant.role === "ai" ||
      !participant.connected
    )
      return false;
    return Boolean(
      liveTrack(this.audioTrack) ||
        liveTrack(this.videoTrack) ||
        participant.microphone ||
        participant.camera,
    );
  }

  private ensurePeer(participantId: string): PeerState {
    const existing = this.peers.get(participantId);
    if (existing) return existing;
    const connection = new RTCPeerConnection({ iceServers: DIRECT_ICE_SERVERS });
    const peer: PeerState = {
      connection,
      stream: new MediaStream(),
      senders: {},
      makingOffer: false,
      ignoreOffer: false,
      settingRemoteAnswer: false,
      polite: this.options.participantId.localeCompare(participantId) > 0,
      pendingCandidates: [],
      reconnectTimer: undefined,
      remoteWantsVideo: true,
      localWantsRemoteVideo: true,
      mediaWatchTimer: undefined,
    };
    this.peers.set(participantId, peer);
    connection.onicecandidate = ({ candidate }) => {
      if (candidate) this.send(participantId, { candidate: candidate.toJSON() });
    };
    connection.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        await connection.setLocalDescription();
        if (connection.localDescription)
          this.send(participantId, { description: connection.localDescription.toJSON() });
      } catch (error) {
        this.report(error, "The direct call offer failed");
      } finally {
        peer.makingOffer = false;
      }
    };
    connection.ontrack = ({ track }) => {
      for (const oldTrack of peer.stream.getTracks().filter((item) => item.kind === track.kind))
        peer.stream.removeTrack(oldTrack);
      peer.stream.addTrack(track);
      track.addEventListener("ended", () => {
        peer.stream.removeTrack(track);
        this.emitRemoteStreams();
        this.watchExpectedRemoteMedia(participantId, peer);
      });
      this.emitRemoteStreams();
      this.watchExpectedRemoteMedia(participantId, peer);
    };
    connection.onconnectionstatechange = () => {
      if (connection.connectionState === "connected") {
        if (peer.reconnectTimer) {
          clearTimeout(peer.reconnectTimer);
          peer.reconnectTimer = undefined;
        }
        void this.applySenderProfile(peer);
        this.watchExpectedRemoteMedia(participantId, peer);
      }
      if (["failed", "disconnected"].includes(connection.connectionState)) {
        if (peer.reconnectTimer) clearTimeout(peer.reconnectTimer);
        peer.reconnectTimer = setTimeout(
          () => this.rebuildPeer(participantId),
          connection.connectionState === "failed" ? 500 : 5_000,
        );
      }
    };
    connection.oniceconnectionstatechange = () => {
      if (["failed", "disconnected"].includes(connection.iceConnectionState))
        this.schedulePeerRebuild(participantId, peer, connection.iceConnectionState === "failed");
    };
    peer.reconnectTimer = setTimeout(() => {
      if (!["connected", "closed"].includes(connection.connectionState))
        this.rebuildPeer(participantId);
    }, 12_000);
    return peer;
  }

  private async syncLocalTracks(peer: PeerState): Promise<void> {
    for (const kind of ["audio", "video"] as const) {
      const sourceTrack = liveTrack(kind === "audio" ? this.audioTrack : this.videoTrack);
      const track = kind === "video" && !peer.remoteWantsVideo ? undefined : sourceTrack;
      const sender = peer.senders[kind];
      if (sender) {
        if (sender.track !== track) await sender.replaceTrack(track ?? null);
        if (!sourceTrack) {
          peer.connection.removeTrack(sender);
          delete peer.senders[kind];
        }
      } else if (track) {
        peer.senders[kind] = peer.connection.addTrack(track, new MediaStream([track]));
      }
    }
    await this.applySenderProfile(peer);
  }

  private peerCount(): number {
    return [...this.participants.values()].filter(
      (participant) =>
        participant.id !== this.options.participantId &&
        participant.role !== "ai" &&
        participant.connected,
    ).length;
  }

  private async applyCaptureProfile(): Promise<void> {
    const track = liveTrack(this.videoTrack);
    if (!track?.applyConstraints) return;
    const profile = getPeerGroupMediaProfile(this.peerCount());
    try {
      await track.applyConstraints({
        width: { ideal: profile.captureWidth },
        height: { ideal: profile.captureHeight },
        aspectRatio: { ideal: 1 },
        frameRate: { ideal: profile.maxFramerate, max: profile.maxFramerate },
      });
    } catch {
      // Some iOS camera tracks reject live resizing; RTP scaling still caps the upload.
    }
  }

  private async applySenderProfile(peer: PeerState): Promise<void> {
    const profile = getPeerGroupMediaProfile(this.peerCount());
    for (const kind of ["audio", "video"] as const) {
      const sender = peer.senders[kind];
      if (!sender?.track) continue;
      try {
        const parameters = sender.getParameters();
        parameters.encodings = parameters.encodings?.length ? parameters.encodings : [{}];
        if (kind === "video") parameters.degradationPreference = "balanced";
        for (const encoding of parameters.encodings) {
          encoding.maxBitrate = kind === "audio" ? profile.audioBitrate : profile.videoBitrate;
          if (kind === "video") {
            encoding.maxFramerate = profile.maxFramerate;
            encoding.scaleResolutionDownBy = profile.scaleResolutionDownBy;
          }
        }
        await sender.setParameters(parameters);
      } catch {
        // Safari may reject sender tuning before negotiation; retry once connected.
      }
    }
  }

  private rebuildPeer(participantId: string): void {
    const participant = this.participants.get(participantId);
    this.closePeer(participantId);
    if (!this.stopped && participant && this.shouldConnect(participant)) {
      void this.syncLocalTracks(this.ensurePeer(participantId));
    }
  }

  private schedulePeerRebuild(participantId: string, peer: PeerState, immediate: boolean): void {
    if (peer.reconnectTimer) clearTimeout(peer.reconnectTimer);
    peer.reconnectTimer = setTimeout(
      () => this.rebuildPeer(participantId),
      immediate ? 500 : 5_000,
    );
  }

  private watchExpectedRemoteMedia(participantId: string, peer: PeerState): void {
    const participant = this.participants.get(participantId);
    const expectsAudio = participant?.microphone === true;
    const expectsVideo = participant?.camera === true && peer.localWantsRemoteVideo;
    const hasAudio = peer.stream.getAudioTracks().some((track) => track.readyState === "live");
    const hasVideo = peer.stream.getVideoTracks().some((track) => track.readyState === "live");
    const missingExpectedTrack = (expectsAudio && !hasAudio) || (expectsVideo && !hasVideo);
    if (!missingExpectedTrack) {
      if (peer.mediaWatchTimer) clearTimeout(peer.mediaWatchTimer);
      peer.mediaWatchTimer = undefined;
      this.mediaRecoveryAttempted.delete(participantId);
      return;
    }
    if (
      peer.mediaWatchTimer ||
      this.mediaRecoveryAttempted.has(participantId) ||
      peer.connection.connectionState !== "connected"
    )
      return;
    peer.mediaWatchTimer = setTimeout(() => {
      peer.mediaWatchTimer = undefined;
      const currentPeer = this.peers.get(participantId);
      if (!currentPeer || currentPeer !== peer) return;
      const currentParticipant = this.participants.get(participantId);
      const stillMissingAudio =
        currentParticipant?.microphone === true &&
        !peer.stream.getAudioTracks().some((track) => track.readyState === "live");
      const stillMissingVideo =
        currentParticipant?.camera === true &&
        peer.localWantsRemoteVideo &&
        !peer.stream.getVideoTracks().some((track) => track.readyState === "live");
      if (!stillMissingAudio && !stillMissingVideo) return;
      this.mediaRecoveryAttempted.add(participantId);
      this.rebuildPeer(participantId);
    }, 6_000);
  }

  private closePeer(participantId: string, emit = true): void {
    const peer = this.peers.get(participantId);
    if (!peer) return;
    if (peer.reconnectTimer) clearTimeout(peer.reconnectTimer);
    if (peer.mediaWatchTimer) clearTimeout(peer.mediaWatchTimer);
    peer.connection.ontrack = null;
    peer.connection.onicecandidate = null;
    peer.connection.onnegotiationneeded = null;
    peer.connection.onconnectionstatechange = null;
    peer.connection.oniceconnectionstatechange = null;
    peer.connection.close();
    this.peers.delete(participantId);
    if (emit) this.emitRemoteStreams();
  }

  private send(targetId: string, signal: Omit<PeerMediaSignal, "fromId" | "targetId">): void {
    if (!signal.description && !signal.candidate && !signal.preferences) return;
    const identifiedSignal = { ...signal, signalId: signal.signalId ?? runtimeId() };
    void Promise.resolve(this.options.signal(targetId, identifiedSignal))
      .then((delivered) => {
        if (!delivered)
          this.options.onError("The room connection could not deliver the direct call signal");
      })
      .catch((error) =>
        this.report(error, "The room connection could not deliver the call signal"),
      );
  }

  private publishLocalState(): void {
    const audio = liveTrack(this.audioTrack);
    const video = liveTrack(this.videoTrack);
    this.options.onPublished({
      ...(audio || video ? { mediaSessionId: `p2p:${this.options.participantId}` } : {}),
      ...(audio ? { audioTrackId: "p2p-audio" } : {}),
      ...(video ? { videoTrackId: "p2p-video" } : {}),
    });
  }

  private emitRemoteStreams(): void {
    const streams = new Map<string, MediaStream>();
    for (const [participantId, peer] of this.peers) {
      if (peer.stream.getTracks().some((track) => track.readyState === "live"))
        streams.set(participantId, peer.stream);
    }
    this.options.onRemoteStreams(streams);
  }

  private report(error: unknown, fallback: string): void {
    this.options.onError(error instanceof Error && error.message ? error.message : fallback);
  }
}
